import { useEffect, useState } from "react";
import { Building2, Save, CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loading } from "@/pages/_shared";

const ENTITY_TYPES = ["LLC", "Corporation (C-Corp)", "S-Corporation", "Sole Proprietorship", "Partnership", "Non-Profit"];

const FIELDS = [
  { key: "legal_name", label: "Legal business name", required: true, ph: "Acme Ventures LLC" },
  { key: "dba", label: "DBA / Trade name", ph: "Acme" },
  { key: "entity_type", label: "Entity type", required: true, type: "select", options: ENTITY_TYPES },
  { key: "formation_state", label: "Formation state", required: true, ph: "Texas" },
  { key: "formation_year", label: "Year formed", ph: "2019" },
  { key: "industry", label: "Industry", required: true, ph: "Freight & Logistics" },
  { key: "ein_last4", label: "EIN (last 4 digits)", required: true, ph: "4821", maxLength: 4 },
  { key: "phone", label: "Business phone", required: true, ph: "(555) 010-0142" },
  { key: "email", label: "Business email", ph: "ops@yourbusiness.com" },
  { key: "website", label: "Website", ph: "yourbusiness.com" },
  { key: "employees", label: "Employees", ph: "12" },
  { key: "address_line", label: "Street address", required: true, ph: "100 Main St" },
  { key: "address_suite", label: "Suite / Unit", ph: "Ste 200" },
  { key: "city", label: "City", required: true, ph: "Houston" },
  { key: "state", label: "State", required: true, ph: "TX" },
  { key: "zip", label: "ZIP code", required: true, ph: "77002" },
];

export default function BusinessProfile() {
  const [profile, setProfile] = useState(null);
  const [completeness, setCompleteness] = useState(0);
  const [missing, setMissing] = useState([]);
  const [saving, setSaving] = useState(false);

  const apply = (data) => {
    const p = data.profile || {};
    if (!Array.isArray(p.owners) || p.owners.length === 0) p.owners = [{ name: "", pct: "" }];
    setProfile(p);
    setCompleteness(data.completeness);
    setMissing(data.missing || []);
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => apply(data)).catch(() => apply({ profile: {} }));
  }, []);

  if (!profile) return <Loading />;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setOwner = (i, k, v) => setProfile((p) => {
    const owners = [...(p.owners || [])];
    owners[i] = { ...owners[i], [k]: v };
    return { ...p, owners };
  });
  const addOwner = () => setProfile((p) => ({ ...p, owners: [...(p.owners || []), { name: "", pct: "" }] }));
  const removeOwner = (i) => setProfile((p) => ({ ...p, owners: (p.owners || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...profile, owners: (profile.owners || []).filter((o) => o.name && o.name.trim()) };
      const { data } = await api.patch("/profile", payload);
      apply(data);
      toast.success("Business profile saved");
    } catch {
      toast.error("Could not save your profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const done = completeness >= 100;

  return (
    <div className="animate-fade-up" data-testid="business-profile-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Business Profile</h1>
      <p className="mt-2 text-[15px] text-slate-500">This is your business — keep it accurate. We use it to show your real next steps. Your information is saved to your account.</p>

      {/* Completeness */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="profile-completeness">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-slate-700">Profile completeness</span>
          <span className="font-head text-[15px] font-extrabold text-[#1a5fd0]">{completeness}%</span>
        </div>
        <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[#1a5fd0] transition-all" style={{ width: `${completeness}%` }} />
        </div>
        {done ? (
          <div className="mt-3 flex items-center gap-2 text-[13px] text-emerald-600"><CheckCircle2 size={15} /> All required details are complete.</div>
        ) : (
          <div className="mt-3 text-[12px] text-slate-500">Still needed: <span className="text-slate-700">{missing.join(", ")}</span></div>
        )}
      </div>

      {/* Fields */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2"><Building2 size={16} className="text-[#1a5fd0]" /><h2 className="font-head text-[15px] font-bold text-slate-900">Business details</h2></div>
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === "legal_name" || f.key === "address_line" ? "sm:col-span-2" : ""}>
              <label className="mb-1 block text-[12px] font-medium text-slate-600">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
              {f.type === "select" ? (
                <select value={profile[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`profile-${f.key}`}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 outline-none focus:border-[#1a5fd0]">
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input value={profile[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.ph} maxLength={f.maxLength} data-testid={`profile-${f.key}`}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px] text-slate-800 outline-none focus:border-[#1a5fd0]" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Owners */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-head text-[15px] font-bold text-slate-900">Beneficial owners <span className="text-red-500">*</span></h2>
          <button onClick={addOwner} data-testid="add-owner-btn" className="flex items-center gap-1 text-[13px] font-semibold text-[#1a5fd0] hover:underline"><Plus size={14} /> Add owner</button>
        </div>
        <p className="mb-3 text-[12px] text-slate-500">List each individual who owns 25% or more of the business.</p>
        <div className="space-y-2">
          {(profile.owners || []).map((o, i) => (
            <div key={i} className="flex items-center gap-2" data-testid="owner-row">
              <input value={o.name || ""} onChange={(e) => setOwner(i, "name", e.target.value)} placeholder="Full legal name" data-testid={`owner-name-${i}`}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[14px] outline-none focus:border-[#1a5fd0]" />
              <input value={o.pct || ""} onChange={(e) => setOwner(i, "pct", e.target.value)} placeholder="% owned"
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-[14px] outline-none focus:border-[#1a5fd0]" />
              <button onClick={() => removeOwner(i)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="save-profile-btn"
          className="inline-flex items-center gap-2 rounded-xl bg-[#1a5fd0] px-6 py-2.5 font-head text-[14px] font-bold text-white transition-colors hover:bg-[#1550b8] disabled:opacity-50">
          {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />} Save Profile
        </button>
        <span className="text-[12px] text-slate-400">Saved to your account · persists after sign-out.</span>
      </div>
    </div>
  );
}
